import { claimStates } from "@obsina/domain";
import type { ClaimState } from "@obsina/domain";
import { ControlPlaneError } from "./api";

/**
 * Read-only projections of the `/v1/knowledge/*` surface used by the Open
 * Commitments view. The wire payload is the query service `QueryEnvelope`
 * (packages/query). apps/web validates every consumed field here with
 * fail-closed guards instead of a schema dependency, mirroring the manual
 * validation already used for the control-plane responses.
 */

export const COMMITMENT_STATUSES = [
  "open",
  "fulfilled",
  "overdue",
  "cancelled",
  "unknown",
] as const;

export type CommitmentStatus = (typeof COMMITMENT_STATUSES)[number];

/** Any status not in the fixed vocabulary is shown as `unknown`, never invented. */
export function normalizeCommitmentStatus(value: string): CommitmentStatus {
  return (COMMITMENT_STATUSES as readonly string[]).includes(value)
    ? (value as CommitmentStatus)
    : "unknown";
}

/**
 * `not_in_scope` is a statement about the product, not about the reader: the
 * layer the operation reads is one this build does not produce, so nothing was
 * withheld and nothing will arrive. A surface that folded it into `unknown`
 * would send a member after a permission or freshness problem that does not
 * exist.
 */
export type KnowledgeAnswerStatus = "ok" | "unknown" | "not_in_scope";
export type KnowledgeUnknownReason =
  "coverage_unknown" | "no_authorized_evidence" | "extraction_disabled";

/** Derived layers the control plane says it does not produce at all. */
export type CoverageExcludedLayer = "knowledge_extraction";

export type SourceLocator = Readonly<
  Record<string, string | number | boolean | null>
>;

export interface CommitmentEvidencePointer {
  readonly evidenceId: string;
  readonly documentBlockId: string;
  readonly sourceItemId: string;
}

export interface OpenCommitment {
  readonly commitmentId: string;
  readonly subjectEntityId: string;
  /** Human-readable label for subjectEntityId; null when none was resolvable. */
  readonly subjectEntityName: string | null;
  readonly counterpartyEntityId: string | null;
  /** Human-readable label for counterpartyEntityId; null when none was resolvable. */
  readonly counterpartyEntityName: string | null;
  readonly summary: string;
  readonly dueAt: string | null;
  readonly status: CommitmentStatus;
  /** Preserved verbatim so an out-of-vocabulary status is still inspectable. */
  readonly rawStatus: string;
  /**
   * How far the record stands behind the promise itself, from the API's
   * additive `claimState`. A second axis from {@link status}: `open` says what
   * is still owed, this says whether the source stated it firmly at all.
   *
   * `null` means the responder stated nothing — an API build older than the
   * field. It is emphatically not `supported`: reading absence as confidence is
   * the defect this field closes, so a null renders no mark rather than a
   * reassuring one.
   */
  readonly claimState: ClaimState | null;
  readonly evidence: readonly CommitmentEvidencePointer[];
}

/**
 * The coarse channel an evidence excerpt was drawn from, used only to label the
 * row with a source-type chip. Derived from the API's additive `sourceKind`
 * field; only the primary content channels map, so an attachment, document or
 * (for now) a WhatsApp message — or any unrecognized/missing value — yields
 * null, an unlabeled row rather than a wrong label.
 */
export type EvidenceChannel = "mail" | "calendar" | "teams";

/**
 * Which layer a quote came from, mirroring the API's additive `layerKind`.
 * `ocr_derived` is a machine reading of an image: shown verbatim, but never the
 * provider's own words. `null` means the envelope carried no label (an older
 * API, or an unrecognized value), which the inspector treats as "unlabeled" and
 * never as canonical.
 */
export type EvidenceLayerKind = "canonical_native" | "ocr_derived";

export interface CommitmentEvidence {
  readonly evidenceId: string;
  readonly documentBlockId: string;
  readonly sourceItemId: string;
  readonly documentVersionId: string;
  readonly title: string;
  readonly quote: string;
  readonly occurredAt: string | null;
  readonly locator: SourceLocator;
  /** The evidence's source channel, or null when none is resolvable. */
  readonly channel: EvidenceChannel | null;
  /** The layer this quote belongs to, or null when the API attached no label. */
  readonly layerKind: EvidenceLayerKind | null;
  /** Engine confidence (0..1) for an OCR-derived quote; null otherwise. */
  readonly derivedConfidence: number | null;
}

/**
 * Whether waiting can still move a coverage gap, mirroring the query package's
 * `CoverageDurability` (apps/web deliberately does not depend on that package,
 * so the union is restated here and re-validated on the wire).
 *
 * `terminal` means every attempt this population gets has been spent or the
 * limit is a policy one, so a surface must not imply that waiting resolves it;
 * `transient` means the pipeline still has work queued for it; `mixed` is both
 * and stays distinct from `terminal` because such a population is still moving.
 * A durability other than `settled` is still `unknown`, never complete.
 */
export type CoverageDurability = "settled" | "transient" | "terminal" | "mixed";

/**
 * How much of an answer's coverage is established, mirroring the query package's
 * `CoverageState` (apps/web deliberately does not depend on that package, so the
 * union is restated here and re-validated on the wire).
 *
 * This union used to carry two values, and everything that was not `complete`
 * was folded into `unknown`. That fold is what emptied these screens: the read
 * boundary answers `partial` when every required source is reconciled and only a
 * derived layer over their content — an unread attachment, a document never read
 * for claims — is incomplete. Such an answer ships real, authorized,
 * evidence-backed records; only recall is bounded. Collapsing it into `unknown`
 * turned "some attachments are unreadable" into "you have nothing", the negative
 * fact `docs/phase-0/data-contracts.md` forbids.
 *
 * `unknown` keeps its exact meaning: the answer's own basis is not established,
 * so no record may be shown at all.
 */
export type KnowledgeCoverageState = "complete" | "partial" | "unknown";

/**
 * Attachment-reading coverage behind an answer, mirroring the query package's
 * `MediaCoverageSummary`. Only the two totals a surface states are carried: how
 * many OCR-eligible items the authorized population holds, and how many have a
 * live reading behind them. The difference is what a partial answer has not
 * read — never a claim that the content is absent.
 */
export interface MediaCoverageCounts {
  readonly itemCount: number;
  readonly settledCount: number;
}

/**
 * Claim-extraction coverage behind an answer, mirroring the query package's
 * `KnowledgeCoverageSummary`, on the same terms as {@link MediaCoverageCounts}:
 * how many eligible document versions exist and how many were read for claims.
 */
export interface KnowledgeExtractionCounts {
  readonly versionCount: number;
  readonly extractedCount: number;
}

export interface KnowledgeCoverage {
  readonly state: KnowledgeCoverageState;
  readonly asOf: string;
  readonly requiredSourceCount: number;
  readonly reconciledSourceCount: number;
  readonly reasons: readonly string[];
  /**
   * Null when the control plane stated no durability — an API build older than
   * the field. A surface then says nothing about whether waiting helps rather
   * than guessing; an unrecognized value is rejected outright.
   */
  readonly durability: CoverageDurability | null;
  /**
   * Additive; null when the envelope stated no media population at all, which
   * is not the same as "everything was read" and must never be rendered as a
   * count of zero.
   */
  readonly media: MediaCoverageCounts | null;
  /** Additive on the same terms as {@link media}. */
  readonly knowledge: KnowledgeExtractionCounts | null;
  /**
   * Derived layers the answer's coverage was NOT measured against, because this
   * build does not produce them.
   *
   * Empty for an API that states no exclusion, which is also what an older
   * build's envelope yields — and that is the honest reading: no claim of
   * exclusion is not a claim that something is excluded. Unrecognized entries
   * are dropped rather than rejected: a layer this build cannot name is one it
   * has no sentence for, and refusing the whole envelope over it would empty a
   * screen that is otherwise entirely readable.
   */
  readonly excludedLayers: readonly CoverageExcludedLayer[];
}

export interface OpenCommitmentsResult {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly items: readonly OpenCommitment[];
  readonly coverage: KnowledgeCoverage;
  readonly nextCursor: string | null;
}

export interface CommitmentEvidenceResult {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly items: readonly CommitmentEvidence[];
  readonly nextCursor: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function invalid(): never {
  throw new ControlPlaneError(
    "The knowledge surface returned an invalid response.",
  );
}

function requireString(value: unknown): string {
  if (typeof value !== "string") invalid();
  return value;
}

function requireTimestamp(value: unknown): string {
  const timestamp = requireString(value);
  if (!Number.isFinite(Date.parse(timestamp))) invalid();
  return timestamp;
}

function nullableString(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== "string") invalid();
  return value;
}

/**
 * Like nullableString, but also accepts a missing key as null. Used only for
 * fields a repository may not populate yet (subjectEntityName,
 * counterpartyEntityName), so an older envelope that omits the key still
 * renders instead of failing closed.
 */
function optionalNullableString(value: unknown): string | null {
  return value === undefined ? null : nullableString(value);
}

function answerStatus(value: unknown): KnowledgeAnswerStatus {
  if (value === "ok" || value === "unknown" || value === "not_in_scope") {
    return value;
  }
  return invalid();
}

function unknownReason(value: unknown): KnowledgeUnknownReason | null {
  if (value === null) return null;
  if (
    value === "coverage_unknown" ||
    value === "no_authorized_evidence" ||
    value === "extraction_disabled"
  ) {
    return value;
  }
  return invalid();
}

function nonNegativeInteger(value: unknown): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0) {
    invalid();
  }
  return value;
}

function stringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) invalid();
  return value.map((entry) => requireString(entry));
}

function parseLocator(value: unknown): SourceLocator {
  if (!isRecord(value)) return {};
  const entries: [string, string | number | boolean | null][] = [];
  for (const [key, nested] of Object.entries(value)) {
    if (
      nested === null ||
      typeof nested === "string" ||
      typeof nested === "boolean" ||
      (typeof nested === "number" && Number.isFinite(nested))
    ) {
      entries.push([key, nested]);
    }
  }
  return Object.fromEntries(entries);
}

function parseEvidencePointer(value: unknown): CommitmentEvidencePointer {
  if (!isRecord(value)) invalid();
  return {
    evidenceId: requireString(value.evidenceId),
    documentBlockId: requireString(value.documentBlockId),
    sourceItemId: requireString(value.sourceItemId),
  };
}

const CLAIM_STATES: ReadonlySet<string> = new Set(claimStates);

/**
 * The API's additive assertion state, on the same three-answer contract the
 * memory graph reads it with:
 *
 * - absent/null → `null`, carried as "the responder said nothing". It must
 *   never become `supported`; that would be this surface inventing the
 *   confidence the record does not carry.
 * - a value in the frozen `ClaimState` vocabulary → carried verbatim.
 * - anything else → the response is rejected. Both sides import the same union
 *   from `@obsina/domain`, so a value outside it means responder and reader
 *   disagree about what an assertion state is; dropping it would draw an
 *   unreadable state as an ordinary, confident promise.
 */
function optionalClaimState(value: unknown): ClaimState | null {
  if (value === undefined || value === null) return null;
  const state = requireString(value);
  if (!CLAIM_STATES.has(state)) invalid();
  return state as ClaimState;
}

function parseCommitment(value: unknown): OpenCommitment {
  if (!isRecord(value)) invalid();
  const rawStatus = requireString(value.status);
  const evidenceValue = value.evidence;
  if (!Array.isArray(evidenceValue)) invalid();
  return {
    commitmentId: requireString(value.commitmentId),
    subjectEntityId: requireString(value.subjectEntityId),
    subjectEntityName: optionalNullableString(value.subjectEntityName),
    counterpartyEntityId: nullableString(value.counterpartyEntityId),
    counterpartyEntityName: optionalNullableString(
      value.counterpartyEntityName,
    ),
    summary: requireString(value.summary),
    dueAt: nullableString(value.dueAt),
    status: normalizeCommitmentStatus(rawStatus),
    rawStatus,
    claimState: optionalClaimState(value.claimState),
    evidence: evidenceValue.map(parseEvidencePointer),
  };
}

/**
 * Maps the API's additive `sourceKind` (a source_items.source_kind value) to the
 * coarse channel a chip labels. Unknown or missing values return null so an
 * older envelope, or a source kind without a channel chip, renders unlabeled
 * rather than failing closed or asserting a wrong channel.
 */
function evidenceChannel(value: unknown): EvidenceChannel | null {
  switch (value) {
    case "email_message":
      return "mail";
    case "calendar_event":
      return "calendar";
    case "teams_message":
      return "teams";
    default:
      return null;
  }
}

/**
 * The API's additive `layerKind`. An unknown or missing value returns null: the
 * inspector then draws an unlabeled row rather than asserting a layer the API
 * did not vouch for — a derived reading is only ever marked when the API says so.
 */
function evidenceLayerKind(value: unknown): EvidenceLayerKind | null {
  return value === "canonical_native" || value === "ocr_derived" ? value : null;
}

function parseEvidenceRecord(value: unknown): CommitmentEvidence {
  if (!isRecord(value)) invalid();
  const derivedConfidence = value.derivedConfidence;
  return {
    evidenceId: requireString(value.evidenceId),
    documentBlockId: requireString(value.documentBlockId),
    sourceItemId: requireString(value.sourceItemId),
    documentVersionId: requireString(value.documentVersionId),
    title: requireString(value.title),
    quote: requireString(value.quote),
    occurredAt: nullableString(value.occurredAt),
    locator: parseLocator(value.locator),
    channel: evidenceChannel(value.sourceKind),
    layerKind: evidenceLayerKind(value.layerKind),
    derivedConfidence:
      typeof derivedConfidence === "number" &&
      Number.isFinite(derivedConfidence)
        ? derivedConfidence
        : null,
  };
}

interface EnvelopeShape {
  readonly status: KnowledgeAnswerStatus;
  readonly reason: KnowledgeUnknownReason | null;
  readonly items: readonly unknown[];
  readonly coverage: unknown;
  readonly nextCursor: string | null;
}

function parseEnvelope(value: unknown): EnvelopeShape {
  if (!isRecord(value)) invalid();
  const data = value.data;
  let items: readonly unknown[] = [];
  if (data !== null) {
    if (!isRecord(data) || !Array.isArray(data.items)) invalid();
    items = data.items;
  }
  return {
    status: answerStatus(value.status),
    reason: unknownReason(value.reason),
    items,
    coverage: value.coverage,
    nextCursor: nullableString(value.nextCursor),
  };
}

const COVERAGE_DURABILITIES: ReadonlySet<string> = new Set([
  "settled",
  "transient",
  "terminal",
  "mixed",
]);

/**
 * Additive field: an API build that predates it simply states nothing, which is
 * carried as null. A *present* value outside the vocabulary is rejected — a
 * durability we cannot read must never be silently downgraded to "no claim
 * about waiting", which is itself a claim the surface would then render.
 */
function coverageDurability(value: unknown): CoverageDurability | null {
  if (value === undefined || value === null) return null;
  if (typeof value === "string" && COVERAGE_DURABILITIES.has(value)) {
    return value as CoverageDurability;
  }
  return invalid();
}

const COVERAGE_STATES: ReadonlySet<string> = new Set([
  "complete",
  "partial",
  "unknown",
]);

/**
 * Fail closed on a state this build does not know: it becomes `unknown`, the
 * only value that shows no record at all. Reading an unrecognized state as
 * `partial` would release rows against a gate whose meaning we cannot verify.
 */
function coverageState(value: unknown): KnowledgeCoverageState {
  return typeof value === "string" && COVERAGE_STATES.has(value)
    ? (value as KnowledgeCoverageState)
    : "unknown";
}

/**
 * Additive, exactly like {@link coverageDurability}: an absent key states no
 * population and is carried as null, so a surface says nothing instead of
 * printing "0 of 0 read". A *present* but malformed object is rejected.
 */
function mediaCounts(value: unknown): MediaCoverageCounts | null {
  if (value === undefined || value === null) return null;
  if (!isRecord(value)) invalid();
  return {
    itemCount: nonNegativeInteger(value.itemCount),
    settledCount: nonNegativeInteger(value.settledCount),
  };
}

const EXCLUDED_LAYERS: ReadonlySet<string> = new Set(["knowledge_extraction"]);

/** See {@link KnowledgeCoverage.excludedLayers} for why an unknown entry drops. */
function excludedLayers(value: unknown): readonly CoverageExcludedLayer[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) invalid();
  return value.filter(
    (entry): entry is CoverageExcludedLayer =>
      typeof entry === "string" && EXCLUDED_LAYERS.has(entry),
  );
}

function knowledgeCounts(value: unknown): KnowledgeExtractionCounts | null {
  if (value === undefined || value === null) return null;
  if (!isRecord(value)) invalid();
  return {
    versionCount: nonNegativeInteger(value.versionCount),
    extractedCount: nonNegativeInteger(value.extractedCount),
  };
}

/**
 * The single coverage parser for every `/v1/knowledge/*` envelope apps/web
 * consumes. It is exported because the memory graph reads the same field off the
 * same responses: it used to keep its own copy, and the copies drifted — the
 * exact failure mode that once left every surface reading a stale watermark
 * fallback. One parser, one vocabulary, one place to widen.
 */
export function parseKnowledgeCoverage(value: unknown): KnowledgeCoverage {
  if (!isRecord(value)) invalid();
  return {
    state: coverageState(value.state),
    asOf: requireTimestamp(value.asOf),
    requiredSourceCount: nonNegativeInteger(value.requiredSourceCount),
    reconciledSourceCount: nonNegativeInteger(value.reconciledSourceCount),
    reasons: stringArray(value.reasons),
    durability: coverageDurability(value.durability),
    media: mediaCounts(value.media),
    knowledge: knowledgeCounts(value.knowledge),
    excludedLayers: excludedLayers(value.excludedLayers),
  };
}

export function parseOpenCommitmentsResponse(
  value: unknown,
): OpenCommitmentsResult {
  const envelope = parseEnvelope(value);
  return {
    status: envelope.status,
    reason: envelope.reason,
    items: envelope.items.map(parseCommitment),
    coverage: parseKnowledgeCoverage(envelope.coverage),
    nextCursor: envelope.nextCursor,
  };
}

export function parseCommitmentEvidenceResponse(
  value: unknown,
): CommitmentEvidenceResult {
  const envelope = parseEnvelope(value);
  return {
    status: envelope.status,
    reason: envelope.reason,
    items: envelope.items.map(parseEvidenceRecord),
    nextCursor: envelope.nextCursor,
  };
}
